import { useCallback, useContext, useState } from 'react';

import { cancelSkillReview, requestDeploy as requestDeployApi, requestSkillReview } from 'client/api';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { i18nWrapper } from 'client/lib/utils';
import { SmartHomeValidationStatuses } from 'client/model/draft';
import { OperationType } from 'client/model/operation';
import { Channel, SmartHome } from 'client/model/skill';
import { makeValidationErrorMessage } from 'client/utils/draft-actions';
import { createOperation } from 'client/utils/operations';
import * as keyset from 'i18n/DraftControlPanel';

const t = i18nWrapper(keyset);

interface ActionState {
    error: string;
    hasPendingAction: boolean;
    statuses?: SmartHomeValidationStatuses;
}

interface UseSmartHomeModerationActionsParams {
    aliceApp: SmartHome;
    safeToRequestReview: boolean;
    afterReviewRequest: () => void;
    refreshAliceApp: () => void;
}

const getValidationStatuses = (error: unknown) => {
    if (!error || typeof error !== 'object' || !('payload' in error)) {
        return undefined;
    }

    const { payload } = error;
    if (!payload || typeof payload !== 'object' || !('statuses' in payload)) {
        return undefined;
    }

    return payload.statuses as SmartHomeValidationStatuses | undefined;
};

export const useSmartHomeModerationActions = ({
    aliceApp,
    safeToRequestReview,
    afterReviewRequest,
    refreshAliceApp,
}: UseSmartHomeModerationActionsParams) => {
    const { refreshSnapshot } = useContext(SnapshotContext);
    const { addOperation } = useContext(CurrentAliceAppContext)!;
    const [{ error, hasPendingAction, statuses }, setState] = useState<ActionState>({
        error: '',
        hasPendingAction: false,
    });
    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);

    const executeAction = useCallback(
        async (action: (_id: string, _channel: Channel) => Promise<unknown>, onDone?: () => void) => {
            setState({ hasPendingAction: true, error: '' });

            try {
                await action(aliceApp.id, aliceApp.channel);
                await refreshSnapshot();
                setState({ hasPendingAction: false, error: '' });
                onDone?.();
                return true;
            } catch (e) {
                const message = makeValidationErrorMessage(aliceApp.channel, e);
                await refreshSnapshot();
                setState({
                    hasPendingAction: false,
                    error: message,
                    statuses: getValidationStatuses(e),
                });
                return false;
            }
        },
        [aliceApp.id, aliceApp.channel, refreshSnapshot],
    );

    const cancelReview = useCallback(() => {
        return executeAction(cancelSkillReview, () => {
            addOperation(createOperation({ aliceApp, type: OperationType.SkillWithdrawn }));
            refreshAliceApp();
        });
    }, [executeAction, addOperation, aliceApp, refreshAliceApp]);

    const requestDeploy = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(t('unsaved-alert'));

        if (!changesDiscarded) {
            return false;
        }

        return executeAction(requestDeployApi, () => {
            addOperation(createOperation({ aliceApp, type: OperationType.DeployRequested }));
            refreshAliceApp();
        });
    }, [safeToRequestReview, executeAction, addOperation, aliceApp, refreshAliceApp]);

    const requestReview = useCallback(() => {
        const changesDiscarded = safeToRequestReview || window.confirm(t('unsaved-alert'));

        if (!changesDiscarded) {
            return;
        }

        setConfirmModalIsOpen(true);
    }, [safeToRequestReview]);

    const onConfirm = useCallback(async () => {
        await executeAction(requestSkillReview, () => {
            addOperation(createOperation({ aliceApp, type: OperationType.ReviewRequested }));
            refreshAliceApp();
        });

        afterReviewRequest();
        setConfirmModalIsOpen(false);
    }, [executeAction, addOperation, aliceApp, refreshAliceApp, afterReviewRequest]);

    const actions = {
        cancelReview,
        requestDeploy,
        requestReview,
    };

    return { actions, error, hasPendingAction, statuses, confirmModalIsOpen, setConfirmModalIsOpen, onConfirm };
};
